/**
 * Клиентское зеркало правил профрисков (api-contract §3f / canon service-prof-risks). Чистые
 * функции — тестируются изолированно (см. profrisk.test.ts). Те же правила сервер применяет
 * авторитетно на submit; здесь — для мгновенной подсветки/блокировки в визарде и гейта выгрузки.
 */
import { isValidEmail, isValidPhone } from '@/lib/validation'
import type { PaletteVariant, StatusRole } from '@/design-system/tokens/status'
import type {
  ChecklistValue,
  ProfriskChecklist,
  ProfriskContact,
  ProfriskDashboardRow,
  ProfriskInstruction,
  ProfriskInstructionFile,
  ProfriskInstructionsDeveloped,
  ProfriskInstructionsMethod,
  ProfriskPosition,
  RiskLevel,
  VehicleUse,
} from '@/lib/api'
import { isUnspecifiedDepartment } from '@/lib/department'

// --- Фабрики пустых сущностей (локальное состояние визарда) ---

export function emptyChecklist(): ProfriskChecklist {
  return {
    firstAid: '',
    evacuationPlan: '',
    laborSafetyTraining: '',
    firstAidInstruction: '',
    fireSafetyBriefing: '',
  }
}

export function emptyContact(): ProfriskContact {
  return { personId: '', fullName: '', phone: '', email: '' }
}

/** Пустая строка ручного списка инструкций (centrika-docs!25: только «Наименование инструкции»). */
export function emptyInstruction(): ProfriskInstruction {
  return { name: '' }
}

// --- Контактное лицо (centrika-docs!27) ---

/** Обязательное поле блока «Контактное лицо» (ключ совпадает с полем `ProfriskContact`). */
export type ProfriskContactField = 'fullName' | 'phone' | 'email'

const PROFRISK_CONTACT_FIELDS: readonly ProfriskContactField[] = ['fullName', 'phone', 'email']

/**
 * Тексты ошибок незаполненных полей контактного лица. Требование (centrika-docs!27) задаёт
 * обязательность, но не тексты — формулировки взяты в тоне уже существующих в мастере
 * («Выберите способ передачи списка», «Выберите ответственного» шага СОУТ) и совпадают с
 * сообщениями сервера (`dto_profrisk.go`), чтобы клиентская проверка была зеркалом серверной.
 */
export const PROFRISK_CONTACT_ERRORS: Record<ProfriskContactField, string> = {
  fullName: 'Выберите контактное лицо',
  phone: 'Укажите телефон',
  email: 'Укажите e-mail',
}

/**
 * Незаполненные обязательные поля контактного лица (centrika-docs!27: «ФИО», «Телефон»,
 * «E-mail» обязательны). Значение из одних пробелов считается незаполненным.
 */
export function profriskContactMissing(contact: ProfriskContact): ProfriskContactField[] {
  return PROFRISK_CONTACT_FIELDS.filter((f) => (contact[f] ?? '').trim() === '')
}

/** Тексты ошибок ФОРМАТА — те же, что показывают подсказки полей и сервер (`domain.ValidPhoneRU`). */
export const PROFRISK_CONTACT_FORMAT_ERRORS: Record<'phone' | 'email', string> = {
  phone: '+7 и 10 цифр',
  email: 'Нужны «@» и точка',
}

/**
 * Заполненные, но НЕВАЛИДНЫЕ по формату поля контакта (ишью #2 → #3): формат телефона и e-mail
 * сервер проверяет при сохранении контакта и отвечает 422, но раньше это не мешало перейти на
 * следующий шаг — ошибка висела у поля, а «Далее» работала. Клиентская проверка здесь — зеркало
 * серверных `ValidPhoneRU`/`ValidEmailSimple`, поэтому гейт не зависит от того, успел ли прийти
 * ответ сервера. Пустые значения сюда не попадают: их закрывает `profriskContactMissing`.
 */
export function profriskContactInvalid(contact: ProfriskContact): Array<'phone' | 'email'> {
  const invalid: Array<'phone' | 'email'> = []
  const phone = (contact.phone ?? '').trim()
  if (phone !== '' && !isValidPhone(phone)) invalid.push('phone')
  const email = (contact.email ?? '').trim()
  if (email !== '' && !isValidEmail(email)) invalid.push('email')
  return invalid
}

/** Блок «Контактное лицо» заполнен и валиден (переход со шага «Организация» разрешён). */
export function isProfriskContactComplete(contact: ProfriskContact): boolean {
  return profriskContactMissing(contact).length === 0 && profriskContactInvalid(contact).length === 0
}

// --- Чек-лист «да/нет» (§3f) ---

/** Ключ пункта чек-листа в порядке отображения (§3f). */
export type ChecklistKey = keyof ProfriskChecklist

/**
 * Пункты чек-листа «Что уже есть на предприятии»: ключ + текст вопроса (дословно из
 * centrika-docs!25, «Шаг 2. Анкета»). Порядок фиксирован требованием; ключи хранения не
 * менялись (design D7) — поменялись только видимые тексты.
 */
export const CHECKLIST_ITEMS: Array<{ key: ChecklistKey; label: string }> = [
  { key: 'firstAid', label: 'Аптечка первой помощи' },
  { key: 'evacuationPlan', label: 'План эвакуации' },
  { key: 'laborSafetyTraining', label: 'Обучение по охране труда проведено' },
  { key: 'firstAidInstruction', label: 'Инструкция по оказанию первой помощи' },
  { key: 'fireSafetyBriefing', label: 'Инструктаж по пожарной безопасности проводится' },
]

/** Подписи вариантов ответа чек-листа. */
export const CHECKLIST_VALUE_LABELS: Record<Exclude<ChecklistValue, ''>, string> = {
  yes: 'Да',
  no: 'Нет',
}

/** Пункт отвечен, если значение строго «да» или «нет» (§3f). */
function isAnswered(v: ChecklistValue): boolean {
  return v === 'yes' || v === 'no'
}

/** Ключи неотвеченных пунктов чек-листа (для подсветки незаполненного). */
export function checklistMissing(checklist: ProfriskChecklist): ChecklistKey[] {
  return CHECKLIST_ITEMS.map((i) => i.key).filter((key) => !isAnswered(checklist[key]))
}

/** Чек-лист заполнен, если все 5 пунктов ∈ {yes,no} (§3f). */
export function isChecklistComplete(checklist: ProfriskChecklist): boolean {
  return checklistMissing(checklist).length === 0
}

// --- Блок «Разработанные инструкции по охране труда» (centrika-docs!25) ---

/**
 * Состояние блока инструкций — то, что проверяется разделом «Проверка заполнения»
 * требования. `instructionFiles` — ФАКТИЧЕСКИ загруженные файлы (не флаг в анкете):
 * зеркало серверного правила (design D6), иначе удаление последнего файла оставляло бы
 * анкету «валидной».
 */
export interface ProfriskInstructionsBlock {
  instructionsDeveloped: ProfriskInstructionsDeveloped
  instructionsMethod: ProfriskInstructionsMethod
  instructions: ProfriskInstruction[]
  instructionFiles: Array<Pick<ProfriskInstructionFile, 'id'>>
}

/** Ключ элемента блока инструкций, у которого показывается ошибка (совпадает с полем 422). */
export type ProfriskInstructionsErrorField =
  | 'instructionsDeveloped'
  | 'instructionsMethod'
  | 'instructionFiles'
  | 'instructions'

/**
 * Тексты ошибок блока инструкций — ДОСЛОВНО из требования («Проверка заполнения»);
 * совпадают с сообщениями сервера (`dto_profrisk.go`), чтобы клиентская проверка была
 * зеркалом серверной.
 */
export const PROFRISK_INSTRUCTIONS_ERRORS: Record<ProfriskInstructionsErrorField, string> = {
  instructionsDeveloped: 'Укажите, разработаны ли инструкции по охране труда',
  instructionsMethod: 'Выберите способ передачи списка',
  instructionFiles: 'Загрузите хотя бы один файл со списком инструкций',
  instructions: 'Добавьте хотя бы одну инструкцию',
}

/** Ручной список заполнен, если есть хотя бы одна строка с непустым наименованием. */
export function hasManualInstructions(list: ProfriskInstruction[]): boolean {
  return list.some((row) => row.name.trim() !== '')
}

/**
 * Введены ли данные ТЕКУЩИМ выбранным способом — гейт диалога «Изменить способ передачи
 * списка?» (без данных способ меняется без подтверждения).
 */
export function methodHasData(b: ProfriskInstructionsBlock): boolean {
  if (b.instructionsMethod === 'files') return b.instructionFiles.length > 0
  if (b.instructionsMethod === 'manual') return hasManualInstructions(b.instructions)
  return false
}

/**
 * Введены ли данные ЛЮБЫМ способом — гейт диалога «Указать, что инструкции отсутствуют?»
 * (ответ «Нет» без введённых данных подтверждения не требует).
 */
export function instructionsHaveData(b: ProfriskInstructionsBlock): boolean {
  return b.instructionFiles.length > 0 || hasManualInstructions(b.instructions)
}

/**
 * Незаполненный элемент блока инструкций (первый по порядку проверки требования) или `null`,
 * если блок заполнен. Ответ «Нет» закрывает блок целиком.
 */
export function instructionsBlockError(
  b: ProfriskInstructionsBlock,
): ProfriskInstructionsErrorField | null {
  if (b.instructionsDeveloped !== 'yes' && b.instructionsDeveloped !== 'no') {
    return 'instructionsDeveloped'
  }
  if (b.instructionsDeveloped === 'no') return null
  if (b.instructionsMethod === 'files') {
    return b.instructionFiles.length > 0 ? null : 'instructionFiles'
  }
  if (b.instructionsMethod === 'manual') {
    return hasManualInstructions(b.instructions) ? null : 'instructions'
  }
  return 'instructionsMethod'
}

/** Блок инструкций заполнен (переход к следующему шагу разрешён). */
export function isInstructionsBlockComplete(b: ProfriskInstructionsBlock): boolean {
  return instructionsBlockError(b) === null
}

// --- Должность (§3f) ---

/**
 * Человекочитаемые ярлыки обязательных полей должности (ключи — как в ProfriskPosition.missing);
 * подписи совпадают с полями формы должности (centrika-docs!26).
 */
export const POSITION_FIELD_LABELS: Record<string, string> = {
  departmentName: 'Подразделение',
  position: 'Должность',
  headcount: 'Штатных единиц',
  vehicleUse: 'Выезды на служебном авто',
  equipment: 'Оборудование, материалы, сырьё, инструменты',
}

/**
 * Склонение «должность»/«должности»/«должностей» вместе с числом — счётчик шага
 * «Добавлено: N должностей» (centrika-docs!26, «Сводка по списку»).
 */
export function positionsCountLabel(n: number): string {
  const abs = Math.abs(n)
  const mod10 = abs % 10
  const mod100 = abs % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} должность`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} должности`
  return `${n} должностей`
}

const VEHICLE_USE_VALUES: readonly VehicleUse[] = ['none', 'with_driver', 'self_drive']

function isVehicleUse(v: unknown): v is VehicleUse {
  return typeof v === 'string' && (VEHICLE_USE_VALUES as readonly string[]).includes(v)
}

/**
 * Список незаполненных/невалидных обязательных полей должности (§3f). Зеркало серверного `missing[]`:
 * departmentName, position, headcount>0, vehicleUse∈enum. Инструкции/реквизиты — вне валидации
 * должности. Фаза 2 (§8.3, design D3): «Оборудование, материалы, сырьё, инструменты» —
 * необязательное поле, в missing[] не входит и не блокирует переход/отправку.
 */
export function computePositionMissing(
  pos: Pick<ProfriskPosition, 'departmentName' | 'position' | 'headcount' | 'vehicleUse' | 'equipment'>,
): string[] {
  const m: string[] = []
  // «Не указано» (training-listeners-count) — тоже не заполнено: для профрисков подразделение
  // обязательно.
  if (isUnspecifiedDepartment(pos.departmentName)) m.push('departmentName')
  if (!pos.position.trim()) m.push('position')
  if (!(pos.headcount > 0)) m.push('headcount')
  if (!isVehicleUse(pos.vehicleUse)) m.push('vehicleUse')
  return m
}

/** Валидность должности = нет незаполненных обязательных полей. */
export function isPositionValid(
  pos: Pick<ProfriskPosition, 'departmentName' | 'position' | 'headcount' | 'vehicleUse' | 'equipment'>,
): boolean {
  return computePositionMissing(pos).length === 0
}

// --- Выезды на авто (§3f) ---

/** Подписи вариантов «Выезды на авто» (дословно §3f). */
export const VEHICLE_USE_LABELS: Record<VehicleUse, string> = {
  none: 'Нет',
  with_driver: 'Да, с водителем',
  self_drive: 'Да, за рулём',
}

/** Варианты для селекта «Выезды на авто» в порядке отображения. */
export const VEHICLE_USE_OPTIONS: Array<{ value: VehicleUse; label: string }> = VEHICLE_USE_VALUES.map(
  (value) => ({ value, label: VEHICLE_USE_LABELS[value] }),
)

// --- Выгрузка анкеты профрисков в Excel по шаблону заказчика (profrisk-anketa-export-template) ---

/**
 * Имя файла выгрузки анкеты профрисков: «Анкета профрисков — <номер заказа>.xlsx» — по образцу
 * выгрузки рабочих мест СОУТ. Зеркало серверного `exports.ProfriskAnketaFileName` (файл
 * формирует сервер, но браузеру имя задаёт скачивающий код).
 */
export function profriskAnketaFileName(orderNumber: string): string {
  return `Анкета профрисков — ${orderNumber}.xlsx`
}

/** Сообщение о неудачной выгрузке — тот же текст, что у выгрузки рабочих мест СОУТ. */
export const PROFRISK_ANKETA_EXPORT_ERROR = 'Не удалось скачать файл. Повторите попытку.'

// --- Уровень риска (результат, §3f) ---

/** Подписи уровней риска (дословно §3f). */
export const RISK_LEVEL_LABELS: Record<RiskLevel, string> = {
  high: 'Высокий',
  medium: 'Средний',
  acceptable: 'Допустимый',
  not_assessed: 'Не оценено',
}

/**
 * Маппинг уровня риска → цвет палитры дашборда (§3f):
 * high→red, medium→amber, acceptable→green, not_assessed→gray.
 */
export const RISK_LEVEL_VARIANT: Record<RiskLevel, PaletteVariant> = {
  high: 'red',
  medium: 'amber',
  acceptable: 'green',
  not_assessed: 'gray',
}

/** Семантическая роль уровня риска (для Badge доменных компонентов). */
export const RISK_LEVEL_ROLE: Record<RiskLevel, StatusRole> = {
  high: 'danger',
  medium: 'warning',
  acceptable: 'success',
  not_assessed: 'neutral',
}

export interface RiskLevelView {
  label: string
  role: StatusRole
}

/** Ярлык + цветовая роль уровня риска (для строк таблицы/счётчиков). */
export const RISK_LEVEL_VIEW: Record<RiskLevel, RiskLevelView> = {
  high: { label: RISK_LEVEL_LABELS.high, role: RISK_LEVEL_ROLE.high },
  medium: { label: RISK_LEVEL_LABELS.medium, role: RISK_LEVEL_ROLE.medium },
  acceptable: { label: RISK_LEVEL_LABELS.acceptable, role: RISK_LEVEL_ROLE.acceptable },
  not_assessed: { label: RISK_LEVEL_LABELS.not_assessed, role: RISK_LEVEL_ROLE.not_assessed },
}

// --- Статус оценки строки страницы «Профриски» (centrika-docs!34) ---

/**
 * Статус оценки должности в таблице страницы «Профриски» (centrika-docs!34) — колонка,
 * пришедшая на смену «Уровню риска». Три значения; уровень риска в таблице больше не
 * показывается (он остался в гистограмме сводных показателей).
 */
export type ProfriskAssessmentStatus = 'not_assessed' | 'assessed' | 'in_progress'

/** Подписи статусов оценки (дословно из требования). */
export const ASSESSMENT_STATUS_LABELS: Record<ProfriskAssessmentStatus, string> = {
  not_assessed: 'Не оценено',
  assessed: 'Оценено',
  in_progress: 'В работе',
}

/**
 * Цветовая роль бейджа статуса. Требование задаёт только «статус отображается бейджем»,
 * цвета не задаёт — берём уже принятую в проекте семантику: «Не оценено» серым (как было у
 * одноимённого уровня риска), «Оценено» зелёным (работа по должности закрыта), «В работе»
 * синим (`info` — тем же цветом идут промежуточные состояния заказов).
 */
export const ASSESSMENT_STATUS_ROLE: Record<ProfriskAssessmentStatus, StatusRole> = {
  not_assessed: 'neutral',
  assessed: 'success',
  in_progress: 'info',
}

/**
 * Статус оценки строки дашборда в порядке приоритета (centrika-docs!34):
 * 1. «В работе» — должность в незавершённом заказе; приоритетнее ранее выполненной оценки;
 * 2. «Оценено» — незавершённого заказа нет и оценка есть (результат завершённого заказа
 *    или историческая запись — обе оси уже сведены сервером в `riskLevel`);
 * 3. «Не оценено» — незавершённого заказа нет и оценки нет.
 */
export function profriskAssessmentStatus(
  row: Pick<ProfriskDashboardRow, 'riskLevel' | 'inProgress'>,
): ProfriskAssessmentStatus {
  if (row.inProgress) return 'in_progress'
  return row.riskLevel === 'not_assessed' ? 'not_assessed' : 'assessed'
}

/** Кнопка «Оценить» показывается только на статусе «Не оценено» (centrika-docs!34). */
export function canAssessRisk(row: Pick<ProfriskDashboardRow, 'riskLevel' | 'inProgress'>): boolean {
  return profriskAssessmentStatus(row) === 'not_assessed'
}

// --- Светофор-счётчики дашборда (§3f) ---

export interface RiskCounters {
  high: number
  medium: number
  acceptable: number
  notAssessed: number
}

/** Счётчики строк дашборда по уровню риска (зеркало серверных `counters`, §3f). */
export function countByRiskLevel(rows: Array<Pick<ProfriskDashboardRow, 'riskLevel'>>): RiskCounters {
  const counters: RiskCounters = { high: 0, medium: 0, acceptable: 0, notAssessed: 0 }
  for (const r of rows) {
    if (r.riskLevel === 'high') counters.high += 1
    else if (r.riskLevel === 'medium') counters.medium += 1
    else if (r.riskLevel === 'acceptable') counters.acceptable += 1
    else counters.notAssessed += 1
  }
  return counters
}

// --- Гейт подачи/выгрузки (повторный прогон всех проверок, §3f) ---

export interface ProfriskAnketaValidation {
  /** Всё валидно: заказ можно подать (зеркало серверного `validateProfriskSubmit`). */
  ok: boolean
  /**
   * Можно выгрузить анкету в Excel. Отличается от `ok` ровно на контактное лицо: требование
   * обязательности контакта (centrika-docs!27) касается перехода со шага «Организация» и подачи
   * заказа, выгрузку оно не упоминает. Заказы, отправленные ДО этого требования, могут иметь
   * пустой контакт, а их поля уже недоступны для правки — привяжи выгрузку к `ok`, и такой заказ
   * стало бы невозможно выгрузить, причём без единого способа это исправить.
   */
  canExport: boolean
  /** Блок «Контактное лицо» шага «Организация» заполнен (centrika-docs!27). */
  contactComplete: boolean
  /** Незаполненные поля контактного лица (для ошибок у соответствующих полей). */
  contactMissing: ProfriskContactField[]
  /** Заполненные, но невалидные по формату поля контакта (ишью #3) — тоже блокируют переход. */
  contactInvalid: Array<'phone' | 'email'>
  checklistComplete: boolean
  /** Блок «Разработанные инструкции по охране труда» заполнен. */
  instructionsComplete: boolean
  /** Незаполненный элемент блока инструкций (для ошибки у соответствующего элемента). */
  instructionsError: ProfriskInstructionsErrorField | null
  hasPositions: boolean
  /** Число недозаполненных должностей — сводка шага «Проверка» и гейт перехода. */
  invalidPositionCount: number
}

type ProfriskAnketaLike = ProfriskInstructionsBlock & {
  contact: ProfriskContact
  checklist: ProfriskChecklist
  positions: Array<
    Pick<ProfriskPosition, 'departmentName' | 'position' | 'headcount' | 'vehicleUse' | 'equipment'>
  >
}

/**
 * Повторный прогон всех проверок анкеты профрисков перед подачей/выгрузкой (§3f «Проверки и
 * блокировка перехода»): контактное лицо (ФИО/телефон/e-mail, centrika-docs!27) + чек-лист
 * (все 5 отвечены) + блок «Разработанные инструкции по охране труда» (centrika-docs!25) +
 * ≥1 валидная должность. Реквизиты организации НЕ валидируются (read-only из профиля).
 * Блока «Подтверждение анкеты предприятия» больше нет.
 */
export function validateProfriskAnketa(a: ProfriskAnketaLike): ProfriskAnketaValidation {
  const contactMissing = profriskContactMissing(a.contact)
  // Формат телефона и e-mail тоже входит в гейт: непустое, но неверное значение раньше
  // пропускало на следующий шаг (ишью #3).
  const contactInvalid = profriskContactInvalid(a.contact)
  const contactComplete = contactMissing.length === 0 && contactInvalid.length === 0
  const checklistComplete = isChecklistComplete(a.checklist)
  const instructionsError = instructionsBlockError(a)
  const instructionsComplete = instructionsError === null
  const invalidPositionCount = a.positions.filter((p) => !isPositionValid(p)).length
  const hasPositions = a.positions.length > 0
  const canExport =
    checklistComplete && instructionsComplete && hasPositions && invalidPositionCount === 0
  const ok = canExport && contactComplete
  return {
    ok,
    canExport,
    contactComplete,
    contactMissing,
    contactInvalid,
    checklistComplete,
    instructionsComplete,
    instructionsError,
    hasPositions,
    invalidPositionCount,
  }
}

/** Можно ли подать заказ (полный гейт профрисков, зеркало серверной проверки подачи). */
export function canSubmitProfriskAnketa(a: ProfriskAnketaLike): boolean {
  return validateProfriskAnketa(a).ok
}

/** Можно ли выгрузить анкету в Excel — без требования к контактному лицу (см. `canExport`). */
export function canExportProfriskAnketa(a: ProfriskAnketaLike): boolean {
  return validateProfriskAnketa(a).canExport
}
